/**
 * テナント横断の比較（オペレーター専用）。
 *
 * ## 出どころのある数字しか出さない
 *
 * 出るのは「集計の回答率」と「配信中のルールの本数」だけである。設計の
 * `TenantSummary` には商品数・前回との差・配信した FAQ・最終の活動時刻も
 * あるが、**どれも出どころが決まっていない**ので API にも無い。
 * 数えていない欄は `null` が返り、ここでは「数えていません」と出す。
 * 0 と書かない（0 件だったのか数えていないのかが読めなくなる）。
 *
 * ## 一覧に出ない会社がありうる
 *
 * 一覧は Identity の表の索引から引く。索引の属性がまだ載っていない会社は
 * 出ない。**画面にはその断りを書かない**（埋め直しが済めば消える話で、
 * 常設の注意書きにすると「いつも欠けている」と読める）。
 */

import { useQuery } from "urql";
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";

import { DelegateOpenButton } from "../DelegateOpenButton";
import { HelpIcon } from "../help/index";
import { OperatorTenantSummariesDocument } from "../graphql/generated/graphql";

/** 1 ページの件数。API の上限（25）を超える値を画面から送らない。 */
const PAGE_SIZE = 20;

const NOT_COUNTED = "数えていません";

function formatCoverage(value: number | null | undefined): string {
  if (value == null) return NOT_COUNTED;
  return `${Math.round(value * 100)}%`;
}

function formatCount(value: number | null | undefined): string {
  if (value == null) return NOT_COUNTED;
  return `${value} 本`;
}

export function ComparePage() {
  const [result] = useQuery({
    query: OperatorTenantSummariesDocument,
    variables: { first: PAGE_SIZE },
  });

  const connection = result.data?.operator.tenantSummaries;
  const edges = connection?.edges ?? [];

  return (
    <div className="vd-page">
      <PageHeader
        title="会社の比較"
        description="visumo が預かっている会社を並べます。"
        titleExtra={<HelpIcon slug="tenants/compare" />}
      />
      <div className="vd-stack">
        {result.fetching ? (
          <LoadingState label="会社の一覧を読み込んでいます" />
        ) : result.error ? (
          <ErrorState description="会社の一覧を読み込めませんでした。" />
        ) : edges.length === 0 ? (
          <EmptyState
            title="並べられる会社がありません"
            description="一覧に出る会社がまだありません。"
          />
        ) : (
          <>
            {edges.map((edge) => (
              <Card
                key={edge.node.tenant.id}
                title={edge.node.tenant.label}
                extra={
                  <DelegateOpenButton
                    tenantId={edge.node.tenant.id}
                    tenantLabel={edge.node.tenant.label}
                  />
                }
              >
                <div className="vd-grid">
                  <div>
                    <div className="vd-stat-label">回答できている割合</div>
                    <div className="vd-stat-value">
                      {formatCoverage(edge.node.coverage)}
                    </div>
                  </div>
                  <div>
                    <div className="vd-stat-label">配信中のルール</div>
                    <div className="vd-stat-value">
                      {formatCount(edge.node.rulesRunning)}
                    </div>
                  </div>
                  <div>
                    <div className="vd-stat-label">集計を計算した時刻</div>
                    <div className="vd-stat-value">
                      {edge.node.coverageComputedAt == null
                        ? NOT_COUNTED
                        : formatJstDateTime(edge.node.coverageComputedAt)}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            {connection?.pageInfo.hasNextPage === true ? (
              <p className="vd-stat-note">
                続きがあります。1 度に並べるのは {PAGE_SIZE} 社までです。
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
